import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, RefreshCw, Trash2, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Invitation {
  id: string;
  phone_number: string;
  vendor_name: string;
  invited_role: string;
  invitation_status: string;
  sent_at: string;
  claimed_at?: string;
  claimed_by_user_id?: string;
  message_sent?: string;
}

interface InvitationsTableProps {
  refreshTrigger?: number;
  onInvitationsChange?: () => void;
}

export const InvitationsTable = ({ refreshTrigger, onInvitationsChange }: InvitationsTableProps) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  const fetchInvitations = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('sms_invitations')
        .select('*')
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching invitations:', error);
        toast({
          title: "Error",
          description: "Failed to fetch invitations",
          variant: "destructive",
        });
        return;
      }

      setInvitations(data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast({
        title: "Error",
        description: "Failed to fetch invitations",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [refreshTrigger]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchInvitations();
  };

  const handleDeleteInvitation = async (invitationId: string, phoneNumber: string) => {
    try {
      const { error } = await (supabase as any)
        .from('sms_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      toast({
        title: "Invitation Deleted",
        description: `Invitation for ${phoneNumber} has been deleted. You can now send a new invitation to this number.`,
      });

      // Refresh the list
      fetchInvitations();
      onInvitationsChange?.();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast({
        title: "Error",
        description: "Failed to delete invitation",
        variant: "destructive",
      });
    }
  };

  const handleResetInvitation = async (invitationId: string, phoneNumber: string) => {
    try {
      const { error } = await (supabase as any)
        .from('sms_invitations')
        .update({
          invitation_status: 'pending',
          claimed_at: null,
          claimed_by_user_id: null
        })
        .eq('id', invitationId);

      if (error) throw error;

      toast({
        title: "Invitation Reset",
        description: `Invitation for ${phoneNumber} has been reset to pending status.`,
      });

      // Refresh the list
      fetchInvitations();
      onInvitationsChange?.();
    } catch (error) {
      console.error('Error resetting invitation:', error);
      toast({
        title: "Error",
        description: "Failed to reset invitation",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'claimed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Claimed</Badge>;
      case 'expired':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading invitations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">SMS Invitations</h3>
        <Button 
          onClick={handleRefresh} 
          disabled={isRefreshing}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {invitations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No invitations sent yet
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor Name</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent At</TableHead>
                <TableHead>Claimed At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">{invitation.vendor_name}</TableCell>
                  <TableCell>{invitation.phone_number}</TableCell>
                  <TableCell className="capitalize">{invitation.invited_role}</TableCell>
                  <TableCell>{getStatusBadge(invitation.invitation_status)}</TableCell>
                  <TableCell>{format(new Date(invitation.sent_at), 'MMM dd, yyyy HH:mm')}</TableCell>
                  <TableCell>
                    {invitation.claimed_at 
                      ? format(new Date(invitation.claimed_at), 'MMM dd, yyyy HH:mm')
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete the invitation for {invitation.phone_number}? 
                              This action cannot be undone and will allow you to send a new invitation to this number.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteInvitation(invitation.id, invitation.phone_number)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {invitation.invitation_status === 'claimed' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" title="Reset to pending status">
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Reset Invitation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Reset the invitation for {invitation.phone_number} back to pending status? 
                                This will unlink it from the claimed user and allow it to be claimed again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleResetInvitation(invitation.id, invitation.phone_number)}
                              >
                                Reset
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};